import React, {Fragment, useContext, useEffect, useState} from 'react';

import {Form, FormGroup, Label} from 'reactstrap';

import {useForm} from "react-hook-form";
import {Btn} from "../../../../../AbstractElements";
import Loader from "../../../../../Layout/Loader";
import axiosConfig from "../../../../../Config/axios";
import moment from "moment";
import BackendDropDown from "../../../../../CommonElements/DropDown/BackendDropDown";
import {getApplicationRoute} from "../../../../../Route/RouteIndex";
import AuthorizationContext from "../../../../../_helper/Authorization";
import type {Board, BoardFormValues, BoardFormProps} from "../../Types/BoardTypes";
import type {AuthorizationContextType} from "../../../../../_helper/Authorization/AuthorizationTypes";
import Select from "react-select";
import type {SingleValue} from "react-select";
import "react-datepicker/dist/react-datepicker.css";

const DatePicker = require("react-datepicker").default;

interface OptionItem {
    value: number;
    label: string;
}

const BoardForm: React.FC<BoardFormProps> = ({callBack, board, fixedValues}) => {
     const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
     const [selectedAmount, setSelectedAmount] = useState<OptionItem | null>(null);
     const [selectedSlots, setSelectedSlots] = useState<OptionItem | null>(null);
     const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
     const [collectorIdForLoggedUser, setCollectorIdForLoggedUser] = useState<number | undefined>(undefined);
     const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler, authorizedUser} = authContext || {};
    const userRoles = (authorizedUser?.system_roles || []).map((role: string) => role?.toLowerCase());
    const isSuperAdminUser = userRoles.includes('super-admin');
    const isCollectorUser = userRoles.includes('collector') && !isSuperAdminUser;

    const today = moment().format('YYYY-MM-DD');

    const amountOptions: OptionItem[] = [
        {value: 50, label: '50'},
        {value: 100, label: '100'},
        {value: 500, label: '500'},
        {value: 1000, label: '1000'}
    ];

    const slotsOptions: OptionItem[] = [
        {value: 10, label: '10'},
        {value: 20, label: '20'},
        {value: 50, label: '50'},
        {value: 100, label: '100'}
    ];

    const {
        register, handleSubmit, clearErrors, formState: {errors}, setValue, getValues
    } = useForm<BoardFormValues>({
        mode: 'onChange',
        defaultValues: {
            date: today,
            collector_id: undefined,
            amount: undefined,
            number_of_slots: undefined
        }
    });

    useEffect(() => {
        if (board) {
            if (board.date) {
                setSelectedDate(new Date(board.date));
                setValue('date', board.date);
            }
            setValue('collector_id', board.collector_id);
            if (board.booking_types?.[0]?.amount) {
                const amountValue = board.booking_types[0].amount;
                setValue('amount', amountValue);
                const selectedOpt = amountOptions.find(opt => opt.value === amountValue);
                setSelectedAmount(selectedOpt || null);
            }
            if (board.number_of_slots) {
                const slotsValue = board.number_of_slots;
                setValue('number_of_slots', slotsValue);
                const selectedOpt = slotsOptions.find(opt => opt.value === slotsValue);
                setSelectedSlots(selectedOpt || null);
            }
        }
    }, [board, setValue])

    useEffect(() => {
        if (!isCollectorUser || !authorizedUser?.id) return;
        setLoaderVisibility(true);
        axiosConfig
            .get(getApplicationRoute('collector'), {
                params: {
                    user_id: authorizedUser.id,
                    page: 1,
                    per_page: 1,
                }
            })
            .then((response: any) => {
                const collectorResponse = response?.data?.data;
                const collectors = Array.isArray(collectorResponse?.data)
                    ? collectorResponse.data
                    : (Array.isArray(collectorResponse) ? collectorResponse : []);
                const matchedCollector = collectors.find((collector: any) => {
                    return Number(collector?.user_id) === Number(authorizedUser.id);
                }) || collectors[0];

                if (matchedCollector?.id) {
                    setCollectorIdForLoggedUser(matchedCollector.id);
                    setValue('collector_id', matchedCollector.id);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }, [isCollectorUser, authorizedUser?.id, setValue]);

     const onSubmit = (data: BoardFormValues) => {
        clearErrors();
        // Format date from DatePicker
        if (selectedDate) {
            data.date = moment(selectedDate).format('YYYY-MM-DD');
        }
        // Set values from Select components
        if (selectedAmount) {
            data.amount = selectedAmount.value;
        }
        if (selectedSlots) {
            data.number_of_slots = selectedSlots.value;
        }
        if (isCollectorUser && collectorIdForLoggedUser) {
            data.collector_id = collectorIdForLoggedUser;
        }
         setLoaderVisibility(true);
         board ? updateBoard(data) : createBoard(data);
     }

    const createBoard = (data: BoardFormValues) => {
        clearErrors();
        setLoaderVisibility(true);
        axiosConfig
            .post(getApplicationRoute('board'), {...data, ...fixedValues})
            .then((response: { data: { data: Board; }; }) => {
                if (response?.data?.data) {
                    callBack?.(response?.data?.data);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    const updateBoard = (data: BoardFormValues) => {
        clearErrors();
        setLoaderVisibility(true);
        if (!board) return;
        axiosConfig
            .put(`${getApplicationRoute('board')}/${board.id}`, data)
            .then((response: { data: { data: Board; }; }) => {
                if (response?.data?.data) {
                    callBack?.(response?.data?.data);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }
    return (<Fragment>
        <Form onSubmit={handleSubmit(onSubmit)}>
            <Loader visibility={loaderVisibility}/>
            {!isCollectorUser && <BackendDropDown name={'collector_id'} label={'Collector'} nameColumn={'name'}
                              getValues={getValues}
                              register={register}
                              errors={errors} url={'application/collector'} options={{required: true}}/>}
            <FormGroup>
                <Label className='col-form-label m-0 required'>Date</Label>
                <div className='position-relative'>
                    <DatePicker
                        selected={selectedDate}
                        onChange={(date: Date | null) => {
                            setSelectedDate(date);
                            if (date) {
                                setValue('date', moment(date).format('YYYY-MM-DD'));
                            }
                        }}
                        minDate={new Date()}
                        maxDate={moment().add(1, 'day').toDate()}
                        dateFormat="yyyy-MM-dd"
                        showPopperArrow={true}
                        placeholderText="Select a date"
                        className='form-control'
                        wrapperClassName="w-100"
                        calendarClassName="custom-calendar"
                    />
                </div>
                {errors.date && <span className='d-block invalid-feedback'>{errors.date.message}</span>}
            </FormGroup>
            <FormGroup>
                <Label className='col-form-label m-0 required'>Amount</Label>
                <Select
                    value={selectedAmount}
                    options={amountOptions}
                    onChange={(option: SingleValue<OptionItem>) => {
                        setSelectedAmount(option);
                        if (option) {
                            setValue('amount', option.value);
                        }
                    }}
                    placeholder="Select Amount"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isSearchable={false}
                />
                {errors.amount && !selectedAmount && <span className='d-block invalid-feedback'>{errors.amount.message}</span>}
            </FormGroup>
            <FormGroup>
                <Label className='col-form-label m-0 required'>Number of Slots</Label>
                <Select
                    value={selectedSlots}
                    options={slotsOptions}
                    onChange={(option: SingleValue<OptionItem>) => {
                        setSelectedSlots(option);
                        if (option) {
                            setValue('number_of_slots', option.value);
                        }
                    }}
                    placeholder="Select Number of Slots"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isSearchable={false}
                />
                {errors.number_of_slots && !selectedSlots && <span className='d-block invalid-feedback'>{errors.number_of_slots.message}</span>}
            </FormGroup>
            <FormGroup>
                <Btn attrBtn={{
                    className: 'd-block w-100 mt-4',
                    color: 'primary',
                    type: 'submit'
                }}>{board ? 'Update' : 'Create'}</Btn>
            </FormGroup>
        </Form>
    </Fragment>);
};

export default BoardForm;

